import { datadogRum, type FailureReason } from '@datadog/browser-rum';

export type { FailureReason as OperationFailureReason };

export type OperationOptions = {
  /** Distinguishes parallel runs of the same operation, e.g. a row id. Use the same key to end it. */
  key?: string;
  /** Custom attributes attached to the operation event. */
  context?: Record<string, unknown>;
  description?: string;
};

const toSdkOptions = (options?: OperationOptions) =>
  options && {
    operationKey: options.key,
    context: options.context,
    description: options.description,
  };

/**
 * Starts a RUM operation (Operations Monitoring). Every start must be ended
 * with {@link succeedOperation} or {@link failOperation} with the same name
 * and key, otherwise Datadog marks it as failed with `timeout` when the
 * session expires. Prefer {@link withOperation} for async work.
 *
 * Names may contain only letters, digits and `- _ . @ $`, with no whitespace;
 * the `@mint/datadog-operation-name` lint rule checks call sites.
 */
export const startOperation = (name: string, options?: OperationOptions) => {
  datadogRum.startOperation(name, toSdkOptions(options));
};

/** Ends an operation started with {@link startOperation} as successful. */
export const succeedOperation = (name: string, options?: OperationOptions) => {
  datadogRum.succeedOperation(name, toSdkOptions(options));
};

/** Ends an operation started with {@link startOperation} as failed. */
export const failOperation = (
  name: string,
  reason: FailureReason,
  options?: OperationOptions,
) => {
  datadogRum.failOperation(name, reason, toSdkOptions(options));
};

/**
 * Runs `task` as a RUM operation: starts it, then marks it as succeeded when
 * the promise resolves, or failed with `error` when it rejects. The result or
 * the error is passed through unchanged.
 *
 * @example
 * ```ts
 * await withOperation('campaign.create', () => createCampaign(payload));
 * ```
 */
export async function withOperation<T>(
  name: string,
  task: () => Promise<T>,
  options?: OperationOptions,
): Promise<T> {
  startOperation(name, options);
  try {
    const result = await task();
    succeedOperation(name, options);
    return result;
  } catch (error) {
    failOperation(name, 'error', options);
    throw error;
  }
}
